import { clearFilterParams } from '@btime/react-contexts'
import { gql } from '@apollo/client'
import { compose, pathOr, filter } from 'ramda'
import { useCustomToken, useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { accountsClient } from '../../../utils/apollo/api'
import { useFieldsFromApi } from '../../../utils/helpers/fields'
import { useOpenEditUserModal } from '../../../utils/routes/modals/modalsActions'
import { usePromiseQuery } from '../../../utils/apollo/_helpers/promiseQuery'
import LabelIconListItem from '../../../packages/kiti/components/list/LabelIconListItem'
import Line from '../../../packages/kiti/components/helpers/Line'
import Select from '../../../packages/kiti/components/form/Select'
import UserMapPicker from './mapPicker/UserMapPicker'
import UserPickerModal from '../picker/UserPickerModal'
import SelectWithInterpolation from '../../../packages/kiti-editor/components/select/SelectWithInterpolation'
import { cleanWhenEnabledFilterIsSet } from '../../../modules/_helpers/filterByEnabled'

const QUERY = gql`
  query Users($search: String, $page: Int, $filter: UserFilter, $formToken: String) {
    users(search: $search, page: $page, formToken: $formToken, filter: $filter, sort: { field: NAME, type: ASC }) {
      id
      name
      contract {
        id
        fieldValues {
          id
          value
          field {
            id
            name
            type
            configuration
            __typename
          }
          __typename
        }
      }
      __typename
    }
  }
`

const useUsersLoader = (queryFilter, userIdToRemoveFromList) => {
  queryFilter = cleanWhenEnabledFilterIsSet({ ...queryFilter })
  const formToken = useCustomToken('formToken')
  const query = usePromiseQuery(QUERY, { client: accountsClient })
  return (search, page = 1) => {
    return query({
      search,
      page,
      formToken,
      filter: { ...clearFilterParams(queryFilter || {}), ignoreSegments: true }
    })?.then(
      compose(
        filter(user => user.id !== userIdToRemoveFromList),
        pathOr([], ['data', 'users'])
      )
    )
  }
}

const UserSelect = ({
  onChange,
  value,
  address,
  filter,
  userIdToRemoveFromList,
  interpolations = [],
  assign,
  ...props
}) => {
  const { t } = useTranslation('users')
  const [mapOpen, setMapOpen] = React.useState(false)
  const [completeSearchOpen, setCompleteSearchOpen] = React.useState(false)
  const canCreateNew = useSessionPermission('createUser')
  const getOptions = useUsersLoader(filter, userIdToRemoveFromList)
  const openCreateModal = useOpenEditUserModal({ outside: true })
  const { data } = useFieldsFromApi('contracts', 'address')

  const Component = !!interpolations.length ? SelectWithInterpolation : Select

  return (
    <>
      <Component
        value={value} //
        onChange={onChange}
        interpolations={interpolations}
        origin="user"
        assign={assign}
        header={
          <>
            {!!data?.fields?.length && address && (
              <LabelIconListItem
                label={t('searchOnMap')}
                icon="RiAccountPinCircleLine"
                weight="600"
                color="dark2"
                onClick={() => setMapOpen(true)}
              />
            )}
            <LabelIconListItem
              label={t('useCompleteSearch')}
              icon="RiFilterLine"
              iconSize={0.9}
              weight="600"
              color="dark2"
              onClick={() => setCompleteSearchOpen(true)}
            />
            <Line color="dark5" />
          </>
        }
        onCreate={
          canCreateNew &&
          (name => {
            openCreateModal({ onSave: onChange, initialState: { name } })
          })
        }
        options={getOptions}
        {...props}
      />

      <UserMapPicker open={mapOpen} onClose={() => setMapOpen(false)} address={address} onSelect={onChange} />
      <UserPickerModal
        open={completeSearchOpen}
        onClose={() => setCompleteSearchOpen(false)}
        onChange={onChange}
        value={value}
        disableOptions
      />
    </>
  )
}

export default UserSelect
